import React, { useCallback, useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Bar, Doughnut, Pie, HorizontalBar, Line } from 'react-chartjs-2';
import { FiLock, FiCalendar } from 'react-icons/fi';
import DataGrid from 'react-data-grid';
import { Toolbar, Data } from 'react-data-grid-addons';

// @ts-ignore
import CsvDownload from 'react-json-to-csv';

import {
  Container,
  Tabela,
  Info,
  MoreInfo,
  Cards,
  SalesChart,
  Botoes,
} from './styles';

import NavBar from '../../components/NavBar';
import Card from '../../components/Card';
import Header from '../../components/Header';
import { ComboBoxContext } from '../../contexts/ComboBoxContext';

import { useAuth } from '../../hooks/auth';

import api from '../../services/api';

interface Profile {
  serial: string;
  dt_exp_serial: string;
  dateFormatted: string;
}

interface Vendas {
  total: number;
  data: string;
}

interface RecebimentosPorMes {
  dinheiro: number;
  cheque: number;
  crediario: number;
  cartao: number;
  total: number;
  cdEmpresa: string;
}

interface TopProdutos {
  nome: string;
  total: number;
}

interface CurvaABC {
  nome: string;
  faturamento: number;
  total: number;
  percentual: number;
  percentual_acumulado: number;
  classificacao: string;
}

interface Empresa {
  cd: string;
  nm: string;
  cd_empresa: string;
}

const defaultColumnProperties = {
  resizable: true,
};

const selectors = Data.Selectors;

const columns = [
  {
    key: 'Nome',
    name: 'Nome',
    filterable: true,
    width: 300,
  },
  { key: 'Faturamento', name: 'Faturamento' },
  { key: 'Total', name: 'Total', width: 130 },
  { key: 'Percentual', name: 'Percentual', width: 100 },
  {
    key: 'PercentualAcumulado',
    name: 'Percentual Acumulado',
    width: 180,
  },
  {
    key: 'Classificacao',
    name: 'Classificação',
    filterable: true,
    width: 120,
  },
].map((c) => ({ ...c, ...defaultColumnProperties }));

const handleFilterChange = (filter: any) => (filters: any) => {
  const newFilters = { ...filters };
  if (filter.filterTerm) {
    newFilters[filter.column.key] = filter;
  } else {
    delete newFilters[filter.column.key];
  }
  return newFilters;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function getRows(rows: any, filters: any) {
  return selectors.getRows({ rows, filters });
}

const Dashboard: React.FC = () => {
  const [empresa, setEmpresa] = useState<Empresa[]>([
    {
      cd: '',
      nm: '',
    } as Empresa,
  ]);
  const [cdEmpresa, setCdEmpresa] = useState(0);
  const [myProfile, setMyProfile] = useState<Profile>();
  const [produtosDozeMesesData, setProdutosDozeMesesData] = useState({});
  const [produtosDonutSemanaData, setProdutosDonutSemanaData] = useState({});
  const [recebimetosMesData, setRecebimetosMesData] = useState({});
  const [topProdutosData, setTopProdutosData] = useState({});
  const [vendasPorClasseData, setVendasPorClasseData] = useState({});
  const [curvaABCData, setCurvaABCData] = useState({});

  const [topProdutosCSV, setTopProdutosCSV] = useState([] as any);
  const [vendasPorClasseCSV, setVendasPorClasseCSV] = useState([] as any);
  const [curvaABCCSV, setCurvaABCCSV] = useState([] as any);

  const [textoTopProdutos, setTextoTopProdutos] = useState(
    'TOP 10 PRODUTOS - HOJE',
  );

  const [textoVendasPorClasse, setTextoVendasPorClasse] = useState(
    'VENDAS POR CLASSE - HOJE',
  );

  const [textoCurvaABC, setTextoCurvaABC] = useState(
    'CURVA ABC DE (PRODUTOS) - HOJE',
  );

  const [filters, setFilters] = useState({});

  const filteredRows = getRows(curvaABCCSV, filters);

  const { signOut } = useAuth();

  const filtroTop10 = useCallback(
    (filtro: number) => {
      const date = new Date();
      let dt_inicial = date.toISOString().slice(0, 10);
      let dt_final = date.toISOString().slice(0, 10);
      switch (filtro) {
        case 0: {
          // HOJE
          dt_inicial = date.toISOString().slice(0, 10);
          dt_final = date.toISOString().slice(0, 10);
          setTextoTopProdutos('TOP 10 PRODUTOS - HOJE');
          break;
        }
        case 1: {
          // ONTEM
          date.setDate(date.getDate() - 1);
          dt_inicial = date.toISOString().slice(0, 10);
          dt_final = dt_inicial;
          setTextoTopProdutos('TOP 10 PRODUTOS - ONTEM');
          break;
        }
        case 2: {
          // 7 DIAS
          dt_final = date.toISOString().slice(0, 10);
          date.setDate(date.getDate() - 7);
          dt_inicial = date.toISOString().slice(0, 10);
          setTextoTopProdutos('TOP 10 PRODUTOS - 7 DIAS');
          break;
        }
        case 3: {
          // 15 DIAS
          dt_final = date.toISOString().slice(0, 10);
          date.setDate(date.getDate() - 15);
          dt_inicial = date.toISOString().slice(0, 10);
          setTextoTopProdutos('TOP 10 PRODUTOS - 15 DIAS');
          break;
        }
        case 4: {
          // 30 DIAS
          dt_final = date.toISOString().slice(0, 10);
          date.setDate(date.getDate() - 30);
          dt_inicial = date.toISOString().slice(0, 10);
          setTextoTopProdutos('TOP 10 PRODUTOS - 30 DIAS');
          break;
        }
        default:
          dt_inicial = date.toISOString().slice(0, 10);
          dt_final = date.toISOString().slice(0, 10);
          setTextoTopProdutos('TOP 10 PRODUTOS - HOJE');
          break;
      }

      api
        .get<TopProdutos[]>('reports/top-produtos', {
          params: {
            dt_inicial,
            dt_final,
            cdEmpresa,
          },
        })
        .then((response) => {
          const total: number[] = [];
          const nomes: string[] = [];

          const itemsFormatted = [] as any;

          response.data.forEach((produto) => {
            total.push(produto.total);
            nomes.push(produto.nome);
            itemsFormatted.push({
              Nome: produto.nome.replace(/,/g, ''), // remove commas to avoid errors,
              Total: produto.total,
            });
          });

          setTopProdutosCSV(itemsFormatted);

          setTopProdutosData({
            labels: nomes,
            datasets: [
              {
                label: 'Produtos',
                data: total,
                backgroundColor: 'rgba(233, 72, 44,0.9)',
              },
            ],
          });
        })
        .catch((error) => {
          setTopProdutosData({});
          console.log(error);
        });
    },
    [cdEmpresa],
  );

  const filtroVendasPorClasse = useCallback(
    (filtro: number) => {
      const date = new Date();
      let dt_inicial = date.toISOString().slice(0, 10);
      let dt_final = date.toISOString().slice(0, 10);
      switch (filtro) {
        case 0: {
          // HOJE
          dt_inicial = date.toISOString().slice(0, 10);
          dt_final = date.toISOString().slice(0, 10);
          setTextoVendasPorClasse('VENDAS POR CLASSE - HOJE');
          break;
        }
        case 1: {
          // ONTEM
          date.setDate(date.getDate() - 1);
          dt_inicial = date.toISOString().slice(0, 10);
          dt_final = dt_inicial;
          setTextoVendasPorClasse('VENDAS POR CLASSE - ONTEM');
          break;
        }
        case 2: {
          // 7 DIAS
          dt_final = date.toISOString().slice(0, 10);
          date.setDate(date.getDate() - 7);
          dt_inicial = date.toISOString().slice(0, 10);
          setTextoVendasPorClasse('VENDAS POR CLASSE - 7 DIAS');
          break;
        }
        case 3: {
          // 15 DIAS
          dt_final = date.toISOString().slice(0, 10);
          date.setDate(date.getDate() - 15);
          dt_inicial = date.toISOString().slice(0, 10);
          setTextoVendasPorClasse('VENDAS POR CLASSE - 15 DIAS');
          break;
        }
        case 4: {
          // 30 DIAS
          dt_final = date.toISOString().slice(0, 10);
          date.setDate(date.getDate() - 30);
          dt_inicial = date.toISOString().slice(0, 10);
          setTextoVendasPorClasse('VENDAS POR CLASSE - 30 DIAS');
          break;
        }
        default:
          dt_inicial = date.toISOString().slice(0, 10);
          dt_final = date.toISOString().slice(0, 10);
          setTextoVendasPorClasse('VENDAS POR CLASSE - HOJE');
          break;
      }

      api
        .get<TopProdutos[]>('reports/vendas-por-classe', {
          params: {
            dt_inicial,
            dt_final,
            cdEmpresa,
          },
        })
        .then((response) => {
          const total: number[] = [];
          const nomes: string[] = [];

          const itemsFormatted = [] as any;
          response.data.forEach((classe) => {
            total.push(classe.total);
            nomes.push(classe.nome);
            itemsFormatted.push({
              Classe: classe.nome.replace(/,/g, ''), // remove commas to avoid errors,
              Total: classe.total,
            });
          });

          setVendasPorClasseCSV(itemsFormatted);

          setVendasPorClasseData({
            labels: nomes,
            datasets: [
              {
                label: 'Classes',
                data: total,
                backgroundColor: 'rgba(233, 120, 44,0.9)',
              },
            ],
          });
        })
        .catch((error) => {
          setVendasPorClasseData({});
          console.log(error);
        });
    },
    [cdEmpresa],
  );

  const filtroCurvaABC = useCallback(
    (filtro: number) => {
      const date = new Date();
      let dt_inicial = date.toISOString().slice(0, 10);
      let dt_final = date.toISOString().slice(0, 10);
      switch (filtro) {
        case 0: {
          // HOJE
          dt_inicial = date.toISOString().slice(0, 10);
          dt_final = date.toISOString().slice(0, 10);
          setTextoCurvaABC('CURVA ABC DE (PRODUTOS) - HOJE');
          break;
        }
        case 1: {
          // ONTEM
          date.setDate(date.getDate() - 1);
          dt_inicial = date.toISOString().slice(0, 10);
          dt_final = dt_inicial;
          setTextoCurvaABC('CURVA ABC DE (PRODUTOS) - ONTEM');
          break;
        }
        case 2: {
          // 7 DIAS
          dt_final = date.toISOString().slice(0, 10);
          date.setDate(date.getDate() - 7);
          dt_inicial = date.toISOString().slice(0, 10);
          setTextoCurvaABC('CURVA ABC DE (PRODUTOS) - 7 DIAS');
          break;
        }
        case 3: {
          // 15 DIAS
          dt_final = date.toISOString().slice(0, 10);
          date.setDate(date.getDate() - 15);
          dt_inicial = date.toISOString().slice(0, 10);
          setTextoCurvaABC('CURVA ABC DE (PRODUTOS) - 15 DIAS');
          break;
        }
        case 4: {
          // 30 DIAS
          dt_final = date.toISOString().slice(0, 10);
          date.setDate(date.getDate() - 30);
          dt_inicial = date.toISOString().slice(0, 10);
          setTextoCurvaABC('CURVA ABC DE (PRODUTOS) - 30 DIAS');
          break;
        }
        default:
          dt_inicial = date.toISOString().slice(0, 10);
          dt_final = date.toISOString().slice(0, 10);
          setTextoCurvaABC('CURVA ABC (PRODUTOS) - HOJE');
          break;
      }

      api
        .get<CurvaABC[]>('reports/curva-abc', {
          params: {
            dt_inicial,
            dt_final,
            cdEmpresa,
          },
        })
        .then((response) => {
          const percentual_acumulado: number[] = [];
          const nomes: string[] = [];

          const formatter = new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          });

          const itemsFormatted = [] as Array<any>;

          response.data.forEach((produto) => {
            percentual_acumulado.push(produto.percentual_acumulado * 100);
            nomes.push(produto.nome);
            itemsFormatted.push({
              Nome: produto.nome.replace(/,/g, ''), // remove commas to avoid errors,
              Faturamento: formatter.format(produto.faturamento),
              Total: formatter.format(produto.total),
              Percentual: `${(produto.percentual * 100).toFixed(2)}%`,
              PercentualAcumulado: `${(
                produto.percentual_acumulado * 100
              ).toFixed(2)}%`,
              Classificacao: produto.classificacao,
            });
          });

          setCurvaABCCSV(itemsFormatted);

          setCurvaABCData({
            labels: nomes,
            datasets: [
              {
                label: 'Percentual acumulado',
                data: percentual_acumulado,
                backgroundColor: 'rgba(0, 0, 0,0)',
                pointBackgroundColor: (context: any) => {
                  const index = context.dataIndex;
                  const value = context.dataset.data[index];

                  if (value >= 95) {
                    return 'red';
                  }
                  if (value >= 80 && value < 95) {
                    return 'blue';
                  }
                  return 'green';
                },
              },
            ],
          });
        })
        .catch((error) => {
          setCurvaABCData({});
          console.log(error);
        });
    },
    [cdEmpresa],
  );

  useEffect(() => {
    // Profile (SERIAL, DATA DE VALIDADE)
    api
      .get<Profile>('profile')
      .then((response) => {
        const profileFormatted = {
          ...response.data,
          dateFormatted: format(
            parseISO(response.data.dt_exp_serial),
            'dd/MM/yyyy',
          ),
        };

        setMyProfile(profileFormatted);
      })
      .catch((error: Error) => {
        if (error.message.includes('401')) signOut();
        console.log(error);
      });

    // Relatorio de vendas nos ultimos 12 meses
    api
      .get<Vendas[]>('reports/vendas-por-mes', {
        params: {
          cdEmpresa,
        },
      })
      .then((response) => {
        const total: number[] = [];
        const meses: string[] = [];
        const nomeMeses = [
          'Janeiro',
          'Fevereiro',
          'Março',
          'Abril',
          'Maio',
          'Junho',
          'Julho',
          'Agosto',
          'Setembro',
          'Outubro',
          'Novembro',
          'Dezembro',
        ];
        response.data.forEach((month) => {
          total.push(month.total);
          const [nMes, ano] = month.data.split('-');
          meses.push(`${nomeMeses[+nMes - 1]}/${ano}`);
        });

        setProdutosDozeMesesData({
          labels: meses,
          datasets: [
            {
              label: 'Valor vendido no mês',
              data: total,
              backgroundColor: 'rgba(233, 167, 44,0.9)',
            },
          ],
        });
      })
      .catch((error) => {
        // setShowProdutosDozeMeses(false);
        console.log(error);
      });

    // Relatorio de vendas dos ultimos 8 dias
    api
      .get<Vendas[]>('reports/vendas-na-semana', {
        params: {
          cdEmpresa,
        },
      })
      .then((response) => {
        const total: number[] = [];
        const data: string[] = [];

        response.data.forEach((day) => {
          // const options = {
          //   weekday: 'long',
          //   year: 'numeric',
          //   month: 'numeric',
          //   day: 'numeric',
          // };

          const dataFormatada = parseISO(day.data).toLocaleDateString('pt-BR', {
            weekday: 'long',
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
          });

          total.push(day.total);
          data.push(dataFormatada);
          // data.push(format(dataFormatada, 'dd/MM/yyyy'));
        });

        setProdutosDonutSemanaData({
          labels: data,
          datasets: [
            {
              label: 'Venda de produtos nos últimos 8 dias',
              data: total,
              backgroundColor: [
                'rgba(44, 115, 233,0.9)',
                'rgba(233, 72, 44,0.9)',
                'rgba(233, 167, 44,0.9)',
                'rgba(44, 233, 157,0.9)',
                'rgba(233, 120, 44,0.9)',
                'rgba(101, 44, 233,0.9)',
                'rgba(233, 44, 224,0.9)',
                'rgba(20, 255, 90,0.9)',
                'rgba(153, 100, 215,0.9)',
                'rgba(100, 100, 100,0.9)',
              ],
            },
          ],
        });
      })
      .catch((error) => {
        // setShowProdutosDonutSemana(false);
        console.log(error);
      });

    // Relatorio de recebimetos do mês
    api
      .get<RecebimentosPorMes>('reports/recebimentos-por-mes', {
        params: {
          mes: new Date().getMonth() + 1, // 0: jan, 1: fev
          ano: new Date().getFullYear(),
          cdEmpresa,
        },
      })
      .then((response) => {
        setRecebimetosMesData({
          labels: ['Dinheiro', 'Cheque', 'Crediário', 'Cartão'],
          datasets: [
            {
              label: 'Relatório de caixa do mês ',
              data: [
                response.data.dinheiro,
                response.data.cheque,
                response.data.crediario,
                response.data.cartao,
              ],
              backgroundColor: [
                'rgba(44, 115, 233,0.9)',
                'rgba(233, 72, 44,0.9)',
                'rgba(233, 167, 44,0.9)',
                'rgba(44, 233, 157,0.9)',
                'rgba(233, 120, 44,0.9)',
              ],
            },
          ],
        });
      })
      .catch((error) => {
        // setShowRecebimetosMes(false);
        console.log(error);
      });

    // Top 10 produtos 7 dias
    filtroTop10(2);

    // Vendas por classe 7 dias
    filtroVendasPorClasse(2);

    // CURVA ABC 7 dias
    filtroCurvaABC(2);
  }, [filtroTop10, filtroVendasPorClasse, filtroCurvaABC, signOut, cdEmpresa]);

  return (
    <Container>
      <NavBar />

      <div id="page-wrap">
        <ComboBoxContext.Provider
          value={{ empresa, cdEmpresa, setEmpresa, setCdEmpresa }}
        >
          <Header />
        </ComboBoxContext.Provider>
        <Info>
          <Cards>
            <Card
              color="blue"
              title="SERIAL"
              description={
                myProfile ? myProfile.serial : 'Ops, ocorreu algum erro'
              }
              icon={FiLock}
              width="362px"
            />
            <Card
              color="red"
              title="VALIDADE SERIAL"
              description={
                myProfile ? myProfile.dateFormatted : 'Ops, ocorreu algum erro'
              }
              icon={FiCalendar}
              width="362px"
            />
          </Cards>
          <SalesChart>
            <Bar
              data={produtosDozeMesesData}
              options={{
                maintainAspectRatio: false,
                title: {
                  text: 'VENDA DE PRODUTOS NOS ÚLTIMOS 12 MESES',
                  display: true,
                  fontColor: '#e9a72c',
                  fontFamily: 'Roboto Condensed',
                  fontSize: 24,
                  fontStyle: 'bold',
                },
                legend: {
                  onClick: (e) => e.stopPropagation(),
                },
                tooltips: {
                  callbacks: {
                    label: (tooltipItem) => {
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      return tooltipItem.yLabel!.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      });
                    },
                  },
                },
                scales: {
                  yAxes: [
                    {
                      ticks: {
                        autoSkip: true,
                        maxTicksLimit: 5,
                        beginAtZero: true,
                        callback: (value) => {
                          return value.toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          });
                        },
                      },
                      gridLines: {
                        display: true,
                      },
                    },
                  ],
                  xAxes: [
                    {
                      gridLines: {
                        display: false,
                      },
                    },
                  ],
                },
              }}
            />
          </SalesChart>
          <MoreInfo>
            <Doughnut
              data={produtosDonutSemanaData}
              options={{
                maintainAspectRatio: false,
                title: {
                  text: 'VENDA DE PRODUTOS NOS ÚLTIMOS 8 DIAS',
                  display: true,
                  fontColor: '#652CE9',
                  fontFamily: 'Roboto Condensed',
                  fontSize: 24,
                  fontStyle: 'bold',
                },
                tooltips: {
                  callbacks: {
                    label: (tooltipItem, data) => {
                      const dataset = data.datasets![tooltipItem.datasetIndex!];

                      const currentLabel = data.labels![tooltipItem.index!];
                      const currentValue = dataset.data![tooltipItem.index!];

                      return `${currentLabel!}: ${currentValue!.toLocaleString(
                        'pt-BR',
                        {
                          style: 'currency',
                          currency: 'BRL',
                        },
                      )}`;
                    },
                  },
                },
              }}
            />
          </MoreInfo>

          <MoreInfo>
            <Pie
              data={recebimetosMesData}
              options={{
                maintainAspectRatio: false,
                title: {
                  text: 'RECEBIMENTOS NO MÊS CORRENTE',
                  display: true,
                  fontColor: '#2C73E9',
                  fontFamily: 'Roboto Condensed',
                  fontSize: 24,
                  fontStyle: 'bold',
                },
                tooltips: {
                  callbacks: {
                    label: (tooltipItem, data) => {
                      const dataset = data.datasets![tooltipItem.datasetIndex!];

                      const currentLabel = data.labels![tooltipItem.index!];
                      const currentValue = dataset.data![tooltipItem.index!];

                      return `${currentLabel!}: ${currentValue!.toLocaleString(
                        'pt-BR',
                        {
                          style: 'currency',
                          currency: 'BRL',
                        },
                      )}`;
                    },
                  },
                },
              }}
            />
          </MoreInfo>

          <Botoes>
            <div />
            <button
              className="top-produtos"
              onClick={() => filtroTop10(0)}
              type="button"
            >
              HOJE
            </button>
            <button
              className="top-produtos"
              onClick={() => filtroTop10(1)}
              type="button"
            >
              ONTEM
            </button>
            <button
              className="top-produtos"
              onClick={() => filtroTop10(2)}
              type="button"
            >
              7 DIAS
            </button>
            <button
              className="top-produtos"
              onClick={() => filtroTop10(3)}
              type="button"
            >
              15 DIAS
            </button>
            <button
              className="top-produtos"
              onClick={() => filtroTop10(4)}
              type="button"
            >
              30 DIAS
            </button>
            <CsvDownload
              className="top-produtos-download"
              data={topProdutosCSV}
            >
              EXPORTAR
            </CsvDownload>
          </Botoes>
          <MoreInfo>
            <HorizontalBar
              data={topProdutosData}
              options={{
                maintainAspectRatio: false,
                title: {
                  text: textoTopProdutos,
                  display: true,
                  fontColor: '#E9482C',
                  fontFamily: 'Roboto Condensed',
                  fontSize: 24,
                  fontStyle: 'bold',
                },
                legend: {
                  onClick: (e) => e.stopPropagation(),
                },
                tooltips: {
                  callbacks: {
                    label: (tooltipItem) => {
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      return tooltipItem.xLabel!.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      });
                    },
                  },
                },
                scales: {
                  xAxes: [
                    {
                      ticks: {
                        autoSkip: true,
                        maxTicksLimit: 10,
                        beginAtZero: true,
                        callback: (value) => {
                          return value.toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          });
                        },
                      },
                      gridLines: {
                        display: true,
                      },
                    },
                  ],
                  yAxes: [
                    {
                      gridLines: {
                        display: false,
                      },
                    },
                  ],
                },
              }}
            />
          </MoreInfo>

          <Botoes>
            <div />
            <button
              className="vendas-por-classe"
              onClick={() => filtroVendasPorClasse(0)}
              type="button"
            >
              HOJE
            </button>
            <button
              className="vendas-por-classe"
              onClick={() => filtroVendasPorClasse(1)}
              type="button"
            >
              ONTEM
            </button>
            <button
              className="vendas-por-classe"
              onClick={() => filtroVendasPorClasse(2)}
              type="button"
            >
              7 DIAS
            </button>
            <button
              className="vendas-por-classe"
              onClick={() => filtroVendasPorClasse(3)}
              type="button"
            >
              15 DIAS
            </button>
            <button
              className="vendas-por-classe"
              onClick={() => filtroVendasPorClasse(4)}
              type="button"
            >
              30 DIAS
            </button>
            <CsvDownload
              className="vendas-por-classe-download"
              data={vendasPorClasseCSV}
            >
              EXPORTAR
            </CsvDownload>
          </Botoes>
          <MoreInfo>
            <Bar
              data={vendasPorClasseData}
              options={{
                maintainAspectRatio: false,
                title: {
                  text: textoVendasPorClasse,
                  display: true,
                  fontColor: '#E9782C',
                  fontFamily: 'Roboto Condensed',
                  fontSize: 24,
                  fontStyle: 'bold',
                },
                legend: {
                  onClick: (e) => e.stopPropagation(),
                },
                /*
                  onClick: (e, element: any) => {
                    if (element.length !== 0) {
                      // eslint-disable-next-line no-underscore-dangle
                      const ind = element[0]._model.label;
                      console.log(ind);
                    }
                  },
                  */
                tooltips: {
                  callbacks: {
                    label: (tooltipItem) => {
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      return tooltipItem.yLabel!.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      });
                    },
                  },
                },
                scales: {
                  yAxes: [
                    {
                      ticks: {
                        autoSkip: true,
                        maxTicksLimit: 5,
                        beginAtZero: true,
                        callback: (value) => {
                          return value.toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          });
                        },
                      },
                      gridLines: {
                        display: true,
                      },
                    },
                  ],
                  xAxes: [
                    {
                      gridLines: {
                        display: false,
                      },
                    },
                  ],
                },
              }}
            />
          </MoreInfo>

          <Botoes>
            <div />
            <button
              className="curva-abc"
              onClick={() => filtroCurvaABC(0)}
              type="button"
            >
              HOJE
            </button>
            <button
              className="curva-abc"
              onClick={() => filtroCurvaABC(1)}
              type="button"
            >
              ONTEM
            </button>
            <button
              className="curva-abc"
              onClick={() => filtroCurvaABC(2)}
              type="button"
            >
              7 DIAS
            </button>
            <button
              className="curva-abc"
              onClick={() => filtroCurvaABC(3)}
              type="button"
            >
              15 DIAS
            </button>
            <button
              className="curva-abc"
              onClick={() => filtroCurvaABC(4)}
              type="button"
            >
              30 DIAS
            </button>
            <CsvDownload className="curva-abc-download" data={curvaABCCSV}>
              EXPORTAR
            </CsvDownload>
          </Botoes>
          <MoreInfo>
            <Line
              data={curvaABCData}
              options={{
                maintainAspectRatio: false,
                title: {
                  text: textoCurvaABC,
                  display: true,
                  fontColor: '#652CE9',
                  fontFamily: 'Roboto Condensed',
                  fontSize: 24,
                  fontStyle: 'bold',
                },
                legend: {
                  onClick: (e) => e.stopPropagation(),
                },
                tooltips: {
                  callbacks: {
                    label: (tooltipItem) => {
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      const valor: number = tooltipItem.yLabel! as number;
                      return `${valor.toFixed(2)}%`;
                    },
                  },
                },
                scales: {
                  yAxes: [
                    {
                      ticks: {
                        autoSkip: true,
                        maxTicksLimit: 10,
                        beginAtZero: true,
                        max: 100,
                        callback: (value: number) => {
                          return `${value.toFixed(2)}%`;
                        },
                      },
                      gridLines: {
                        display: true,
                      },
                    },
                  ],
                  xAxes: [
                    {
                      gridLines: {
                        display: true,
                      },
                      display: true,
                    },
                  ],
                },
              }}
            />
          </MoreInfo>

          <div className="tabela">
            <Tabela>
              <DataGrid
                columns={columns}
                rowGetter={(i) => filteredRows[i]}
                rowsCount={filteredRows.length}
                minHeight={400}
                toolbar={
                  <Toolbar enableFilter filterRowsButtonText="Filtrar" />
                }
                onAddFilter={(filter) => setFilters(handleFilterChange(filter))}
                onClearFilters={() => setFilters({})}
              />
            </Tabela>
          </div>
        </Info>
      </div>
    </Container>
  );
};

export default Dashboard;
